import React, { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";
import Login from "./pages/Login";
import Layout from "./components/Layout";
import HomeMenu from "./pages/HomeMenu";
import AssembliesPage from "./pages/AssembliesPage";
import AssembliesStartPage from "./pages/AssembliesStartPage";
import MarketplacePickPage from "./pages/MarketplacePickPage";
import OzonAssembliesPage from "./pages/OzonAssembliesPage";
import OzonAssembliesStartPage from "./pages/OzonAssembliesStartPage";
import YmAssembliesPage from "./pages/YmAssembliesPage";
import YmAssembliesStartPage from "./pages/YmAssembliesStartPage";
import ScanHistoryPage from "./pages/ScanHistoryPage";
import ReturnAcceptPage from "./pages/ReturnAcceptPage";
import DefectAcceptPage from "./pages/DefectAcceptPage";
import MarkingTestScanPage from "./pages/MarkingTestScanPage";
import {
  CLIENT_SESSION_EXPIRED_EVENT,
  getClientUser,
  hasClientSession,
} from "./authSession";
import keeperMuiTheme from "./theme/muiTheme";


/**
 * Уводит на ввод пин-кода, как только сессия кончилась.
 *
 * Два случая: сервер ответил 401 (тогда clearClientSession шлёт событие) и срок
 * сессии вышел, пока экран просто висел открытым — его ловим опросом, иначе
 * кладовщик продолжал бы сканировать в пустоту до первого ручного перехода.
 */
function SessionWatcher() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const toLogin = () => {
      if (window.location.pathname === "/login") return;
      navigate("/login", { replace: true });
    };
    window.addEventListener(CLIENT_SESSION_EXPIRED_EVENT, toLogin);
    const id = window.setInterval(() => {
      if (!hasClientSession()) toLogin();
    }, 15000);
    return () => {
      window.removeEventListener(CLIENT_SESSION_EXPIRED_EVENT, toLogin);
      window.clearInterval(id);
    };
  }, [navigate]);

  // Проверяем и при каждом переходе: вкладка могла пролежать в фоне.
  useEffect(() => {
    if (location.pathname !== "/login" && !hasClientSession()) {
      navigate("/login", { replace: true });
    }
  }, [location.pathname, navigate]);

  return null;
}

function ProtectedRoute({ children }) {
  const user = getClientUser();
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider theme={keeperMuiTheme}>
      <CssBaseline />
      <BrowserRouter>
        <SessionWatcher />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<HomeMenu />} />
            <Route path="pick" element={<MarketplacePickPage />} />
            <Route path="assemblies" element={<AssembliesPage />} />
            <Route path="assemblies/start" element={<AssembliesStartPage />} />
            <Route path="ozon/assemblies" element={<OzonAssembliesPage />} />
            <Route path="ozon/assemblies/start" element={<OzonAssembliesStartPage />} />
            <Route path="ym/assemblies" element={<YmAssembliesPage />} />
            <Route path="ym/assemblies/start" element={<YmAssembliesStartPage />} />
            <Route path="returns" element={<ReturnAcceptPage />} />
            <Route path="defect" element={<DefectAcceptPage />} />
            <Route path="marking-test" element={<MarkingTestScanPage />} />
            <Route path="history" element={<ScanHistoryPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
